import { useCallback, useEffect, useMemo, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { hasOpenPopover } from "@design-system/Menu/hasOpenPopover";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useIsLastPressKey } from "@hooks/useIsLastPressKey";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type {
    ClientsWorkOverviewWorkRow,
    ClientsWorkOverviewWorkspaceRow,
} from "../clientsWorkOverviewViewConfig";
import { getWorkRows } from "../board/workBoardColumns";

export interface ActiveWorkItem {
    /** The work item shown in the detail pane, or null when it's closed. */
    activeRow: ClientsWorkOverviewWorkRow | null;
    /** The last open came from a key (Enter on a card or row), so the pane skips its entrance. */
    isOpenedByKeyboard: boolean;
    openWorkItem: (row: ClientsWorkOverviewWorkRow) => void;
    closeWorkItem: () => void;
}

/**
 * The work item open in the detail pane, kept in the URL as `?workId=` so a
 * reload or a shared link reopens it. The param is dropped once the page's
 * rows have loaded without it, e.g. after a filter or page change.
 */
export const useActiveWorkItem = ({
    workspaceRows,
    isLoaded,
}: {
    workspaceRows: readonly ClientsWorkOverviewWorkspaceRow[] | null;
    isLoaded: boolean;
}): ActiveWorkItem => {
    const [{ workId }, setQuery] = useQueryParams({ workId: StringParam });

    const activeRow = useMemo(() => {
        if (!workId || !workspaceRows || isBusyClosesDemoId(workId)) {
            return null;
        }
        return (
            getWorkRows(workspaceRows).find(
                (row) => parseCloseId(row.closeId) === workId,
            ) ?? null
        );
    }, [workId, workspaceRows]);

    const isMissing = !!workId && isLoaded && activeRow === null;
    useEffect(() => {
        if (isMissing) {
            setQuery({ workId: undefined }, "replaceIn");
        }
    }, [isMissing, setQuery]);

    const isLastPressKey = useIsLastPressKey();
    const [isOpenedByKeyboard, setIsOpenedByKeyboard] = useState(false);
    const openWorkItem = useCallback(
        (row: ClientsWorkOverviewWorkRow) => {
            if (isBusyClosesDemoId(row.closeId)) {
                return;
            }
            setIsOpenedByKeyboard(isLastPressKey());
            setQuery({ workId: parseCloseId(row.closeId) }, "replaceIn");
        },
        [setQuery, isLastPressKey],
    );
    const closeWorkItem = useCallback(
        () => setQuery({ workId: undefined }, "replaceIn"),
        [setQuery],
    );

    // Capture runs before the board's own Escape, which then sees the event
    // handled and keeps the selection: the first Escape only closes the pane.
    // An open modal, menu, or popover (the pane's own, or a list cell
    // editor's) takes Escape first.
    const hasOpenModal = useHasOpenModal();
    // Functions, unlike booleans, don't re-register the hotkey when the pane
    // opens or closes; re-registering re-renders every hotkey consumer, the
    // whole app shell included, in the same frame the pane starts to slide.
    const isEscapeActive = () => activeRow !== null && !hasOpenModal;
    useKeyboardCommand({
        keys: "Escape",
        callback: closeWorkItem,
        options: {
            enabled: isEscapeActive,
            ignoreEventWhen: () => hasOpenMenu() || hasOpenPopover(),
            preventDefault: isEscapeActive,
            eventListenerOptions: { capture: true },
            description: "Close work item details",
        },
    });

    return { activeRow, isOpenedByKeyboard, openWorkItem, closeWorkItem };
};
